import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import InventoryManager from '../../components/feature/InventoryManager';
import StockDisplay from '../../components/feature/StockDisplay';
import { type InventoryData } from '../../components/feature/StockDisplay';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';

export default function ProduitBouleNoelArgentPailletee() {
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [inventory, setInventory] = useState<InventoryData | null>(null);
  const productId = 'boule-noel-argent-pailletee';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1953d1495cd5e8d1c233a6c576865ce6.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);

  const product = {
    id: 'boule-noel-argent-pailletee',
    name: 'Boule de Noël argent pailletée',
    price: 4,
    category: 'Collection de Noël',
    subcategory: 'Boules de Noël',
    description: "Boule de Noël artisanale à suspendre ou à poser. Couleur : argent pailleté ✨ Chaque boule est réalisée à la main et peut légèrement varier selon l'inspiration.",
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1953d1495cd5e8d1c233a6c576865ce6.png',
    sku: 'BOU-ARG-04'
  };

  const handleAddToCart = async () => {
    setIsAdding(true);
    
    try {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        description: product.description,
        link: `/produit-boule-noel-argent-pailletee`
      }, quantity);

      // Diminuer le stock si nécessaire
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/inventory-management?action=decrease`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            product_id: product.id,
            quantity
          }),
        }
      );
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
    }

    // Feedback visuel
    setTimeout(() => {
      setIsAdding(false);
    }, 2000);
  };

  const isOutOfStock = () => {
    if (!inventory) return false;
    
    if (inventory.is_made_to_order || inventory.unlimited_stock) {
      return false;
    }
    
    return inventory.stock_quantity === 0;
  };

  const totalPrice = product.price * quantity;

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />
      
      <main className="pt-20">
        {/* Breadcrumb */}
        <section className="py-6 bg-[#FAF9F8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors">
                Accueil
              </Link>
              <span className="text-[#C49B86]">/</span>
              <Link to="/boutique" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors">
                Boutique
              </Link>
              <span className="text-[#C49B86]">/</span>
              <span className="text-[#B97A72] font-medium">Boule de Noël argent pailletée</span>
            </nav>
          </div>
        </section>

        {/* Contenu principal */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Image produit */}
              <div className="space-y-4">
                <div className="aspect-square bg-white rounded-lg border-2 border-[#B97A72] overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Informations produit */}
              <div className="space-y-8">
                
                {/* Titre et prix */}
                <div>
                  <div className="mb-2">
                    <span className="text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.subcategory}
                    </span>
                  </div>
                  <h1 className="text-4xl font-bold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.name}
                  </h1>
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="text-3xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.price}€
                    </span>
                  </div>
                  
                  {/* Affichage du stock */}
                  <StockDisplay 
                    productId={product.id}
                    className="mb-4"
                  />
                </div>

                {/* Description */}
                <div className="prose prose-lg max-w-none">
                  <p className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {product.description}
                  </p>
                </div>

                {/* Gestion des stocks */}
                <InventoryManager
                  productId={product.id}
                  productName={product.name}
                  category={product.category}
                  subcategory={product.subcategory}
                  onInventoryUpdate={setInventory}
                />

                {/* Informations produit */}
                <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Informations produit
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="font-medium text-[#B97A72] w-24" style={{ fontFamily: 'Playfair Display, serif' }}>SKU :</span>
                      <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.sku}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-[#B97A72] w-24" style={{ fontFamily: 'Playfair Display, serif' }}>Type :</span>
                      <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>Décoration de Noël</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-[#B97A72] w-24" style={{ fontFamily: 'Playfair Display, serif' }}>Couleur :</span>
                      <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>Argent pailleté</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-[#B97A72] w-24" style={{ fontFamily: 'Playfair Display, serif' }}>Matière :</span>
                      <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>Artisanal fait main</span>
                    </div>
                  </div>
                </div>

                {/* Sélecteur de quantité et ajout au panier */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <label className="text-lg font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Quantité :
                    </label>
                    <div className="flex items-center border-2 border-[#B97A72] rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="px-4 py-2 text-[#6B5E5A] font-medium min-w-[3rem] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="px-3 py-2 text-[#B97A72] hover:bg-[#F8EDEA] transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xl font-semibold text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Total : {totalPrice}€
                    </span>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    disabled={isAdding || isOutOfStock()}
                    className="w-full bg-white text-[#B97A72] border-2 border-[#B97A72] px-8 py-4 rounded-lg hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {isOutOfStock() ? (
                      <>
                        <i className="ri-close-circle-line mr-2"></i>
                        Rupture de stock
                      </>
                    ) : isAdding ? (
                      <>
                        <i className="ri-check-line mr-2"></i>
                        Ajouté au panier !
                      </>
                    ) : (
                      <>
                        <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                          <i className="ri-shopping-cart-line"></i>
                        </div>
                        Ajouter au panier
                      </>
                    )}
                  </button>
                </div>

                {/* Retour à la boutique */}
                <div className="pt-6 border-t border-[#EBD2CF]">
                  <Link
                    to="/boutique"
                    className="inline-flex items-center text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Retour à la boutique
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Informations de livraison */}
        <section className="py-12 bg-[#EBD2CF]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-hand-heart-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Fait main
                </h3>
                <p className="text-[#6B5E5A] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Création artisanale unique
                </p>
              </div>
              
              <div>
                <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-truck-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Livraison soignée
                </h3>
                <p className="text-[#6B5E5A] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Emballage protégé
                </p>
              </div>
              
              <div>
                <div className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Paiement sécurisé
                </h3>
                <p className="text-[#6B5E5A] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Transactions protégées
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
